"use client";

import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { routes } from "@/routes";
import { useAuth } from "@/providers/auth-provider";
import { useRouter } from "next/navigation";
import { Check, Trophy, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { TierCard, TierIcon } from "@/components/ui-library";

export const HeroSection = () => {
  const { user } = useAuth();
  const router = useRouter();

  const handleGetStarted = () => {
    if (user) {
      router.push(routes.dashboard.root);
    } else {
      router.push(routes.register);
    }
  };

  const handleViewPricing = () => {
    const pricingSection = document.getElementById("pricing-section");
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      className="w-full py-12 md:py-24 lg:py-32 xl:py-48 relative overflow-hidden"
      style={{
        backgroundImage: "url(/bgs/bg1.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-emerald-950/50" />
      <Container className="relative z-10">
        <div className="flex flex-col items-center space-y-6 text-center">
          {/* Badge */}
          <Badge
            variant="outline"
            className="bg-emerald-500/10 border-emerald-500/30 text-emerald-400 px-4 py-1"
          >
            The social network for golfers
          </Badge>

          <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl leading-tight px-4 text-white drop-shadow-lg">
            Challenge Local Golfers. <br className="hidden md:block" />
            Share Your Journey.
          </h1>

          <p className="mx-auto max-w-[700px] text-gray-200 md:text-xl px-4 drop-shadow-md">
            Browse courses for free. Upgrade to Member to challenge golfers,
            join leagues, and share your golf moments.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4 w-full sm:w-auto px-4 sm:px-0">
            <Button
              size="lg"
              className="px-8 py-3 text-lg w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600"
              onClick={handleGetStarted}
            >
              <Trophy className="h-5 w-5 mr-2" />
              Get Started Free
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="px-8 py-3 text-lg w-full sm:w-auto"
              onClick={handleViewPricing}
            >
              View Pricing
            </Button>
          </div>

          {/* Tier Overview */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl w-full px-4">
            <TierCard variant="free">
              <TierIcon icon={Eye} variant="free" size="sm" />
              <span className="font-semibold mt-2">Free</span>
              <span className="text-xs text-muted-foreground">
                Browse courses
              </span>
            </TierCard>

            <TierCard variant="player">
              <TierIcon icon={Trophy} variant="player" size="sm" />
              <span className="font-semibold mt-2 text-emerald-400">
                Member $8.99/mo
              </span>
              <span className="text-xs text-muted-foreground">
                Challenge & connect
              </span>
            </TierCard>
          </div>

          {/* Value Props */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-gray-300 px-4">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              <span>Free to browse courses</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              <span>14-day free trial</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
